import {useEffect, useState} from 'react'
import {useQueryClient} from '@tanstack/react-query'
// /max, not the default "min" metadata — min only checks number LENGTH, so junk like
// +929999999999 passes; /max does the full pattern check.
import {parsePhoneNumberFromString} from 'libphonenumber-js/max'
import OtpInput, {formatOtpTimer} from '@/customComponents/uiComponents/global/OtpInput.jsx'
import FlagSelectWithNumber from '@/customComponents/uiComponents/global/FlagSelectWithNumber.jsx'
import {Loader} from '@/customComponents/uiComponents/Loader.jsx'
import {userService} from '@/services/userService'
import {useAuth} from '@/context/AuthContext'
import {BOOKING_CONSTRAINTS} from '@/constants/booking'
import {identityMissingLabel} from '@/utils/identityGate'

/**
 * COMPLETE A PROFILE THE BOOKING GATE REFUSED — name, email, phone, each the way the profile
 * page does it. `POST /account/complete-identity` is NOT used here any more.
 *
 *   name   →  POST /account (first_name + last_name)
 *   email  →  add-user-email → request-user-email-otp → verify-user-email-otp → set-user-email-default
 *   phone  →  add-user-phone → request-user-phone-otp → verify-user-phone-otp → set-user-phone-default
 *
 * ⚠️ VERIFIED, NOT JUST TYPED. complete-identity stored an email and a phone unverified, so a
 * booking could go out with an address nobody owned. Here the six-digit code proves the
 * contact, and "set default" is what makes it the one the gate (and every confirmation) reads
 * — without it the placeholder stays default and the gate never clears.
 *
 * Only the fields the server listed are asked for, one at a time, in name → email → phone
 * order. Used by the confirm pages' modal and by the booking flow's completeProfile step.
 *
 * @param {string[]} missing      'name' | 'email' | 'phone' — what the server still wants
 * @param {Function} onCompleted  every listed field is done
 * @param {boolean}  showRequired outline the empty inputs in red — set by a page that has just
 *                                refused to continue because of them (see the phone-booking
 *                                confirmation's Reserve Ride)
 */
const ORDER = ['name', 'email', 'phone']
const EMPTY_OTP = () => Array(6).fill('')
const INPUT = 'rounded-full w-full px-5 py-3.5 max-md:py-2.5 max-md:text-sm outline-none bg-transparent border border-(--bg-dark-grey)'
const INPUT_REQUIRED = 'border-(--text-red)!'
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const firstFieldError = (raw, fallback) => {
    const first = raw?.errors && Object.values(raw.errors)[0]?.[0]
    return first || fallback
}
// The profile page treats "already exists" on add as fine — the next step is the code.
// `email_already_in_use` / `phone_already_in_use` = the contact belongs to ANOTHER account. That is
// not "already added here" — carrying on to the code step only ends in "Email not found.".
const takenByAnother = (raw) => /in_use/i.test(raw?.key || '')
const alreadyThere = (message) => /already|exist/i.test(message || '')

export default function IdentityCompletionFlow({missing, onCompleted, showRequired = false}) {
    const queryClient = useQueryClient()
    const {login} = useAuth()
    const [steps] = useState(() => ORDER.filter((field) => (missing || []).includes(field)))
    const [index, setIndex] = useState(0)
    const field = steps[index]

    const [phase, setPhase] = useState('input') // 'input' | 'otp'
    const [firstName, setFirstName] = useState('')
    const [lastName, setLastName] = useState('')
    const [email, setEmail] = useState('')
    const [phone, setPhone] = useState('')
    const [otp, setOtp] = useState(EMPTY_OTP)
    const [resendTimer, setResendTimer] = useState(0)
    const [error, setError] = useState('')
    const [busy, setBusy] = useState(false)

    useEffect(() => {
        if (resendTimer <= 0) return undefined
        const timer = setInterval(() => setResendTimer((prev) => Math.max(prev - 1, 0)), 1000)
        return () => clearInterval(timer)
    }, [resendTimer])

    const next = () => {
        if (index + 1 < steps.length) {
            setIndex(index + 1)
            setPhase('input')
            setOtp(EMPTY_OTP())
            setError('')
            return
        }
        queryClient.invalidateQueries({queryKey: ['user', 'account']})
        onCompleted?.()
    }

    // ── name ────────────────────────────────────────────────────────────────────
    const saveName = async () => {
        if (!firstName.trim() || !lastName.trim()) return setError('First and last name are required')
        setBusy(true)
        setError('')
        const result = await userService.updateAccount({first_name: firstName.trim(), last_name: lastName.trim()})
        setBusy(false)
        if (!result.success) return setError(firstFieldError(result.raw, result.error) || 'Could not save your name.')
        if (result.token) login(result.token)
        next()
    }

    // ── email / phone: add → send code ──────────────────────────────────────────
    const phoneParts = () => {
        const parsed = parsePhoneNumberFromString(phone || '')
        return parsed && parsed.isValid() ? {number: parsed.number, country: parsed.country?.toLowerCase() || null} : null
    }

    const sendCode = async () => {
        setError('')
        if (field === 'email' && !EMAIL_RE.test(email.trim())) return setError('Please enter a valid email address')
        const parts = field === 'phone' ? phoneParts() : null
        if (field === 'phone' && !parts) return setError('Please enter a valid phone number')

        setBusy(true)
        try {
            const target = field === 'email' ? email.trim() : parts.number
            const added = field === 'email'
                ? await userService.addEmail(target)
                : await userService.addPhone(target, parts.country)
            if (!added.success && takenByAnother(added.raw)) {
                setError(`This ${field === 'email' ? 'email address' : 'phone number'} is already linked to another account. Please use a different one.`)
                return false
            }
            if (!added.success && !alreadyThere(firstFieldError(added.raw, added.error))) {
                setError(firstFieldError(added.raw, added.error) || `Failed to add ${field}`)
                return
            }
            const sent = field === 'email'
                ? await userService.requestEmailOtp(target)
                : await userService.requestPhoneOtp(target)
            if (!sent.success) {
                setError(firstFieldError(sent.raw, sent.error) || 'Failed to send code')
                return
            }
            setOtp(EMPTY_OTP())
            setPhase('otp')
            setResendTimer(BOOKING_CONSTRAINTS.OTP_RESEND_TIMER_SECONDS)
        } catch (err) {
            setError(err?.message || 'Unexpected error while sending code')
        } finally {
            setBusy(false)
        }
    }

    // ── email / phone: verify → set default ─────────────────────────────────────
    const verify = async () => {
        setError('')
        const code = otp.join('')
        if (code.length < 6) return setError('Please enter a valid 6-digit code')

        setBusy(true)
        try {
            const target = field === 'email' ? email.trim() : (phoneParts()?.number || phone)
            const verified = field === 'email'
                ? await userService.verifyEmailOtp(target, code)
                : await userService.verifyPhoneOtp(target, code)
            if (!verified.success) {
                setError(firstFieldError(verified.raw, verified.error) || 'Invalid code')
                setOtp(EMPTY_OTP())
                return
            }
            const made = field === 'email'
                ? await userService.setDefaultEmail(target)
                : await userService.setDefaultPhone(target)
            if (!made.success) {
                setError(firstFieldError(made.raw, made.error) || 'Failed to set as default')
                return
            }
            next()
        } catch (err) {
            setError(err?.message || 'Unexpected error while verifying code')
        } finally {
            setBusy(false)
        }
    }

    if (!field) return null

    const primary = field === 'name' ? saveName : (phase === 'input' ? sendCode : verify)
    const primaryLabel = field === 'name'
        ? (index + 1 < steps.length ? 'Save and continue' : 'Save')
        : (phase === 'input' ? 'Send verification code' : 'Verify')
    const target = field === 'email' ? email.trim() : phone

    return (
        <div>
            <p className="text-(--text-secondary) mb-4!">
                We need your {identityMissingLabel(steps)} before you can book — your driver uses it to reach you about the ride.
                {steps.length > 1 && <span className="block mt-1 text-sm">Step {index + 1} of {steps.length}</span>}
            </p>

            {field === 'name' && (
                <div className="flex gap-3 max-md:flex-col">
                    <input type="text" placeholder="First name" value={firstName} className={`${INPUT} ${showRequired && !firstName.trim() ? INPUT_REQUIRED : ''}`}
                           onChange={(ev) => { setFirstName(ev.target.value); setError('') }}/>
                    <input type="text" placeholder="Last name" value={lastName} className={`${INPUT} ${showRequired && !lastName.trim() ? INPUT_REQUIRED : ''}`}
                           onChange={(ev) => { setLastName(ev.target.value); setError('') }}/>
                </div>
            )}

            {field === 'email' && phase === 'input' && (
                <input type="email" placeholder="Email address" value={email} className={`${INPUT} ${showRequired && !email.trim() ? INPUT_REQUIRED : ''}`}
                       onChange={(ev) => { setEmail(ev.target.value); setError('') }}/>
            )}
            {field === 'phone' && phase === 'input' && (
                <FlagSelectWithNumber value={phone} onChange={(value) => { setPhone(value || ''); setError('') }}/>
            )}

            {field !== 'name' && phase === 'otp' && (
                <>
                    <p className="text-(--text-secondary) text-center">
                        We sent a code to <span className="font-bold text-(--text-primary)">{target}</span>
                    </p>
                    <OtpInput value={otp} onChange={(next2) => { setOtp(next2); setError('') }} autoFocus className="my-4"/>
                    <div className="flex items-center justify-center gap-3">
                        <button type="button" onClick={sendCode} disabled={resendTimer > 0 || busy}
                                className={`text-sm font-medium border-0 bg-transparent p-0 ${
                                    resendTimer > 0 || busy ? 'cursor-not-allowed text-gray-400' : 'cursor-pointer text-(--text-primary) hover:opacity-80'
                                }`}>
                            Resend code
                        </button>
                        {resendTimer > 0 && <span className="text-sm text-(--text-secondary)">{formatOtpTimer(resendTimer)}</span>}
                    </div>
                </>
            )}

            {error && <p className="text-red-500 text-sm mt-3 mb-0!">{error}</p>}

            <button type="button" onClick={primary} disabled={busy} className="hopn-add-passenger-button mt-5">
                {busy ? <Loader color="#fff"/> : primaryLabel}
            </button>
        </div>
    )
}
